import { useEffect, useState } from "react";
import { Play, Pause, SkipForward } from "lucide-react";
import { useMusic, SONGS } from "@/music";
import { useLang } from "@/i18n";
import { playBg, toggleBg, subscribeBg, isBgPlaying, setPlaylist, setBgLoop, bgFile } from "@/lib/audioBus";

const PLAYABLE = SONGS.filter((s) => !s.noFile);

// Landing-screen music controller. It drives the persistent background-music
// singleton (audioBus) so the chosen track keeps playing on Character/Level
// select even though this control only renders on the title screen.
export default function TitleMusic() {
  const { songs } = useMusic();
  const { t } = useLang();
  const [playing, setPlaying] = useState(isBgPlaying());
  // Pick a random track each time the landing page loads ("auto").
  const [autoId] = useState(() => PLAYABLE[Math.floor(Math.random() * PLAYABLE.length)].id);
  // Home selection is session-local and ALWAYS starts on "auto" so the song
  // rotates on every load — intentionally NOT persisted.
  const [sel, setSel] = useState("auto");

  const resolvedId = sel === "auto" ? autoId : sel;
  const song = SONGS.find((s) => s.id === resolvedId) || SONGS[0];
  const options = [{ id: "auto", name: t("autoMusic") }, ...songs];

  // Register the playlist once and turn OFF single-track looping so songs
  // auto-advance to the next one throughout the whole app.
  useEffect(() => { setPlaylist(PLAYABLE.map((s) => s.file)); setBgLoop(false); }, []);

  // Keep the displayed title in sync as tracks change (manual or auto-advance).
  const [curF, setCurF] = useState(bgFile());
  useEffect(() => subscribeBg(() => { setPlaying(isBgPlaying()); setCurF(bgFile()); }), []);
  const dispSong = SONGS.find((s) => s.file === curF) || song;

  // Load + (re)play whenever the chosen song changes.
  useEffect(() => { playBg(song.file).then(() => setPlaying(isBgPlaying())); }, [song.file]);

  // Browsers block autoplay-with-sound until a gesture — start on first tap/key.
  useEffect(() => {
    let done = false;
    const tryPlay = () => {
      if (done) return;
      playBg(song.file).then(() => { if (isBgPlaying()) { done = true; setPlaying(true); cleanup(); } });
    };
    const cleanup = () => {
      window.removeEventListener("pointerdown", tryPlay);
      window.removeEventListener("keydown", tryPlay);
      window.removeEventListener("touchstart", tryPlay);
    };
    playBg(song.file).then(() => {
      if (isBgPlaying()) setPlaying(true);
      else {
        window.addEventListener("pointerdown", tryPlay);
        window.addEventListener("keydown", tryPlay);
        window.addEventListener("touchstart", tryPlay);
      }
    });
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = () => { toggleBg(); setPlaying(isBgPlaying()); };

  // Jump to the next track in the playable list (wraps around).
  const nextSong = () => {
    const ids = PLAYABLE.map((s) => s.id);
    const i = ids.indexOf(resolvedId);
    setSel(ids[(i + 1) % ids.length]);
  };

  return (
    <div className="flex items-center gap-1.5 rounded-xl bg-black/65 backdrop-blur-md ring-1 ring-white/15 px-2 py-1.5 shrink-0" data-testid="title-music">
      <button
        onClick={toggle}
        data-testid="title-music-toggle"
        aria-label={playing ? "Pause music" : "Play music"}
        className="shrink-0 w-8 h-8 rounded-full bg-[#FF5722] text-white flex items-center justify-center active:scale-95 transition-transform"
      >
        {playing ? <Pause size={14} strokeWidth={2.5} fill="#fff" /> : <Play size={14} strokeWidth={2.5} fill="#fff" className="ml-0.5" />}
      </button>

      <button
        onClick={nextSong}
        data-testid="title-music-next"
        aria-label="Next song"
        title="Next song"
        className="shrink-0 w-7 h-7 rounded-full bg-white/10 ring-1 ring-white/20 text-white flex items-center justify-center active:scale-95 transition-transform"
      >
        <SkipForward size={12} strokeWidth={2.5} fill="#fff" />
      </button>

      <div className="min-w-0 flex flex-col leading-none text-left">
        <p className="font-vt text-xs text-[#FFEB3B] leading-none truncate max-w-[7rem]" data-testid="title-music-title">{dispSong.title || dispSong.name}</p>
        <select
          data-testid="music-select"
          value={sel}
          onChange={(e) => setSel(e.target.value)}
          className="font-vt text-[10px] leading-none bg-transparent text-white/70 cursor-pointer max-w-[7.5rem] truncate outline-none mt-0.5"
        >
          {options.map((s) => (
            <option key={s.id} value={s.id} style={{ color: "#000" }} data-testid={`song-${s.id}`}>
              {s.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
